import type { ProductProjection } from '@unity/commerce-bridge';

/**
 * Master-variant attribute PIM writes on every sellable commercetools product
 * it creates: the numeric Asset Store product id (compass-services
 * `pim_product_request_builder.go`, `externalReferenceID: base.ProductID`).
 */
export const EXTERNAL_REFERENCE_ID_ATTRIBUTE = 'externalReferenceId';

const ASSET_STORE_PRODUCT_ID = /^\d+$/;

/** Products synced straight to commercetools before PIM: `assetstore-{id}`. */
const LEGACY_PRODUCT_KEY = /^assetstore-(\d+)$/;

/**
 * Only a bare number is an Asset Store id. Non-sellable package products carry
 * package names in the same attribute, and old staging data `id:uuid:uuid`
 * composites; either would "save" an id the lists API cannot render.
 */
const toAssetStoreProductId = (value: unknown): string | undefined => {
  const candidate = typeof value === 'number' ? String(value) : value;
  return typeof candidate === 'string' && ASSET_STORE_PRODUCT_ID.test(candidate)
    ? candidate
    : undefined;
};

export const assetStoreProductIdFromProjection = (
  product: ProductProjection | undefined
): string | undefined =>
  toAssetStoreProductId(
    product?.masterVariant?.attributes?.find(
      // Not in commerce-bridge's AttributeName union: PIM adds it.
      ({ name }) => String(name) === EXTERNAL_REFERENCE_ID_ATTRIBUTE
    )?.value
  );

export const assetStoreProductIdFromProductKey = (
  productKey: string | undefined
): string | undefined => LEGACY_PRODUCT_KEY.exec(productKey ?? '')?.[1];

/**
 * Maps a commercetools product to the Asset Store product id our lists API
 * keys on: the projection's `externalReferenceId` first, then a legacy
 * `assetstore-{id}` product key. `undefined` when neither holds one.
 */
export const resolveAssetStoreProductId = (
  product: ProductProjection | undefined,
  productKey: string | undefined
): string | undefined =>
  assetStoreProductIdFromProjection(product) ??
  assetStoreProductIdFromProductKey(productKey ?? product?.key);
